import React, { useEffect, useState } from "react";
import type { User } from "firebase/auth";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import TextField from "@mui/material/TextField";
import { shopAdminApi, ShopApiError } from "./shopAdminApi";
import { retryBlockLabels, type KustomRetryCheck } from "./kustomInspectionModel";
import { useConfirmedShopMutation } from "./useConfirmedShopMutation";

export default function WebshopKustomRetry({ user, jobId, onClose, onRequested }: {
  user: User; jobId: string; onClose: () => void; onRequested: () => void;
}) {
  const [check, setCheck] = useState<KustomRetryCheck | null>(null), [error, setError] = useState("");
  const [reason, setReason] = useState(""), [refresh, setRefresh] = useState(0);
  const mutation = useConfirmedShopMutation(onRequested, user?.uid);
  useEffect(() => {
    let live = true; setCheck(null); setError("");
    shopAdminApi.kustomRetryCheck(user, jobId).then((value) => { if (live) setCheck(value); })
      .catch((failure) => { if (live) setError(failure instanceof ShopApiError && [401, 403].includes(failure.status) ?
        "Behörighet till avläsningsåtgärden saknas eller har återkallats." : "Kontrollen kunde inte hämtas. Försök igen."); });
    return () => { live = false; };
  }, [user, jobId, refresh]);
  const reload = () => { if (mutation.pending) return; mutation.reset(); setReason(""); setRefresh((value) => value + 1); };
  const submit = () => {
    if (!check?.eligible || !reason.trim() || mutation.pending || mutation.conflict) return;
    const body = { expectedDocumentVersion: check.expectedDocumentVersion, reason: reason.trim() }, actor = user;
    void mutation.submit((key) => shopAdminApi.requestKustomRetry(actor, jobId, body, key));
  };
  return <Dialog open fullWidth maxWidth="sm" onClose={mutation.pending ? undefined : onClose} aria-labelledby="kustom-retry-title">
    <DialogTitle id="kustom-retry-title">Begär ny Kustom-avläsning</DialogTitle>
    <DialogContent className="WebshopOfferDialog WebshopKustomRecovery">
      <p>Jobbreferens: {jobId}</p>
      <Alert severity="info">Åtgärden registrerar en ny begränsad avläsningsomgång. Den gör ingen betalning, återbetalning eller lagerändring.
        Den schemalagda återhämtningen kontrollerar kön normalt var femte minut; detta är ingen bekräftelse på ett godkänt köp.</Alert>
      {error && <Alert severity="error">{error}</Alert>}
      {!check && !error && <p role="status">Kontrollerar aktuell avläsningsrätt…</p>}
      {check && <>
        <p>Aktivt konto: {check.activeAccount ? `${check.activeAccount.kustomEnvironment === "playground" ? "Playground (test)" : "Produktion (live)"} · MID ${check.activeAccount.merchantId}` : "Okänt"}</p>
        <p>Tidigare begärda omgångar: {check.reviewCycles ?? "okänt"} / {check.maxReviewCycles} · Försök i tidigare omgångar: {check.priorReadbackAttempts ?? "okänt"}
          · Försök i aktuell omgång: {check.attempts ?? "okänt"} / 8</p>
        {check.blockReason && <Alert severity="warning">{retryBlockLabels[check.blockReason]}</Alert>}
        {check.eligible && <TextField fullWidth multiline minRows={2} label="Utredningsskäl för ny avläsning" value={reason}
          inputProps={{ maxLength: 500 }} disabled={mutation.pending || mutation.conflict} onChange={(event) => setReason(event.target.value)}
          helperText="Beskriv vad som har utretts eller åtgärdats. Skälet sparas i en privat revisionspost; skriv inga nycklar eller kunduppgifter." />}
      </>}
      {mutation.error && <Alert severity="error">{mutation.error}</Alert>}
      {mutation.pending && !mutation.saving && <Alert severity="warning">Begäran är låst till samma konto, uppgifter och nyckel tills resultatet är bekräftat.</Alert>}
    </DialogContent>
    <DialogActions className="WebshopContentActions">
      <Button onClick={onClose} disabled={mutation.pending}>Tillbaka till jobbet</Button>
      <Button onClick={reload} disabled={mutation.pending}>Hämta ny kontroll</Button>
      {mutation.pending ? <Button onClick={mutation.retry} disabled={mutation.saving}>Bekräfta samma begäran igen</Button> :
        <Button onClick={submit} disabled={!check?.eligible || !reason.trim() || mutation.conflict}>Begär avläsning</Button>}
    </DialogActions>
  </Dialog>;
}
